import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "../../utils/axiosInstance";

const DESTINATION_METHODS = {
  CA: ["INTERAC"],

  CM: ["MOBILE_MONEY", "BANK_TRANSFER"],
  CF: ["MOBILE_MONEY", "BANK_TRANSFER"],
  TD: ["MOBILE_MONEY", "BANK_TRANSFER"],
  CG: ["MOBILE_MONEY", "BANK_TRANSFER"],
  GA: ["MOBILE_MONEY", "BANK_TRANSFER"],
  GQ: ["MOBILE_MONEY", "BANK_TRANSFER"],

  BJ: ["MOBILE_MONEY", "BANK_TRANSFER"],
  BF: ["MOBILE_MONEY", "BANK_TRANSFER"],
  CI: ["MOBILE_MONEY", "BANK_TRANSFER"],
  GW: ["MOBILE_MONEY", "BANK_TRANSFER"],
  ML: ["MOBILE_MONEY", "BANK_TRANSFER"],
  NE: ["MOBILE_MONEY", "BANK_TRANSFER"],
  SN: ["MOBILE_MONEY", "BANK_TRANSFER"],
  TG: ["MOBILE_MONEY", "BANK_TRANSFER"],

  CD: ["MOBILE_MONEY", "BANK_TRANSFER"],
  GH: ["MOBILE_MONEY", "BANK_TRANSFER"],

  NG: ["BANK_TRANSFER"],
};

const up = (c, fallback) => String(c || fallback || "").toUpperCase();

export default function AmountStep({ formData, setFormData, onNext, onPrev }) {
  const {
    countryFrom,
    countryTo,
    currency,
    recipientCurrency,
    mode = "SEND",
    amountToSend,
    amountToReceive,
    method,
    calculation,
  } = formData;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const reqIdRef = useRef(0);

  const currencyFromU = up(currency, "CAD");
  const recipientCurrencyU = up(recipientCurrency, "XAF");

  const methods = useMemo(() => {
    if (!countryTo) return [];
    return DESTINATION_METHODS[up(countryTo)] || [];
  }, [countryTo]);

  useEffect(() => {
    if (methods.length && !methods.includes(method)) {
      setFormData((p) => ({ ...p, method: methods[0], calculation: null }));
    }
  }, [methods, method, setFormData]);

  useEffect(() => {
    if (!formData.mode) setFormData((p) => ({ ...p, mode: "SEND" }));
  }, [formData.mode, setFormData]);

  const sourceAmount = mode === "SEND" ? amountToSend : amountToReceive;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setError("");

    const input = Number(sourceAmount || 0);
    if (
      !countryFrom ||
      !countryTo ||
      !method ||
      !recipientCurrencyU ||
      input <= 0
    )
      return;

    const t = setTimeout(() => callCalc(input), 450);
    return () => clearTimeout(t);
  }, [
    mode,
    sourceAmount,
    method,
    countryFrom,
    countryTo,
    currencyFromU,
    recipientCurrencyU,
  ]);

  const callCalc = async (inputAmount) => {
    const myReqId = ++reqIdRef.current;

    setLoading(true);
    setError("");

    try {
      const payload = {
        mode,
        amount: Number(inputAmount),
        currencyFrom: currencyFromU,
        recipientCurrency: recipientCurrencyU,
        method,
        destinationCountry: up(countryTo),
        countryFrom: up(countryFrom),
      };

      const res = await axios.post("/api/transactions/calc", payload);
      const data = res.data || {};

      if (myReqId !== reqIdRef.current) return;

      const normalized = {
        exchange_rate: Number(data.exchange_rate || 0),
        convertedAmount: Number(data.convertedAmount || 0),
        fee: Number(data.fee || 0),
        totalToPay: Number(data.totalToPay || 0),
        currencyFrom: up(data.currencyFrom, currencyFromU),
        recipientCurrency: up(data.recipientCurrency, recipientCurrencyU),
        amountToSend: Number(data.amountToSend || 0),
        mode: data.mode || mode,
        feePercent: Number(data.feePercent || 0),
        ruleCode: data.ruleCode || "",
        rateSource: data.rateSource || "fixed",
        exchange_timestamp: data.exchange_timestamp || null,
      };

      setFormData((p) => {
        const next = { ...p, calculation: normalized };

        if (mode === "SEND") {
          next.amountToReceive = String(normalized.convertedAmount);
        } else {
          next.amountToSend = String(normalized.amountToSend);
        }

        return next;
      });
    } catch (err) {
      if (myReqId !== reqIdRef.current) return;
      console.error("calc err:", err?.response?.data || err?.message || err);
      setError(
        err?.response?.data?.message ||
          "Impossible de calculer pour l’instant.",
      );
      setFormData((p) => ({ ...p, calculation: null }));
    } finally {
      if (myReqId === reqIdRef.current) setLoading(false);
    }
  };

  if (!countryFrom || !countryTo || countryFrom === countryTo) {
    return (
      <div className="rounded-2xl border border-black/5 dark:border-white/10 bg-slate-50 dark:bg-white/5 p-4 min-w-0 w-full max-w-full">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Veuillez d’abord sélectionner le pays d’envoi et de réception.
        </p>
        <button
          type="button"
          onClick={onPrev}
          className="mt-3 w-full sm:w-auto rounded-2xl px-4 py-2 bg-slate-900 text-white dark:bg-white dark:text-slate-900"
        >
          Retour
        </button>
      </div>
    );
  }

  const displayRate = Number(calculation?.exchange_rate || 0);

  const corridorHint = (() => {
    const from = up(countryFrom);
    const to = up(countryTo);

    // NEW RULE (PRIORITY)
    if ((from === "CA" && to === "CM") || (from === "CM" && to === "CA")) {
      return "Canada ⇄ Cameroun : frais 0%.";
    }

    // Canada → other Africa
    if (
      from === "CA" &&
      [
        "CF",
        "TD",
        "CG",
        "GA",
        "GQ",
        "BJ",
        "BF",
        "CI",
        "GW",
        "ML",
        "NE",
        "SN",
        "TG",
      ].includes(to)
    ) {
      return "Canada → Afrique XAF/XOF : taux 410, frais 2%.";
    }

    // Africa → Canada (EXCLUDING Cameroon now)
    if (
      [
        "CF",
        "TD",
        "CG",
        "GA",
        "GQ",
        "BJ",
        "BF",
        "CI",
        "GW",
        "ML",
        "NE",
        "SN",
        "TG",
      ].includes(from) &&
      to === "CA"
    ) {
      return "Afrique XAF/XOF → Canada : base 435, frais 2%.";
    }

    // Nigeria
    if (from === "NG" || to === "NG") {
      return "Nigéria : frais 1% dans les deux directions, retrait par banque uniquement.";
    }

    // DRC
    if (from === "CD" || to === "CD") {
      return "RDC : valeur temporaire fixe avant branchement API live.";
    }

    // Ghana
    if (from === "GH" || to === "GH") {
      return "Ghana : valeur temporaire fixe avant branchement API live.";
    }

    // Africa → Africa
    if (from !== "CA" && to !== "CA") {
      return "Afrique → Afrique XAF/XOF : taux 1:1, frais 5%.";
    }

    return null;
  })();
  const canContinue = Boolean(
    calculation?.totalToPay && calculation?.convertedAmount,
  );

  const PillButton = ({ active, title, desc, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      className={`w-full min-w-0 rounded-full border px-4 py-3 text-left transition overflow-hidden ${
        active
          ? "border-slate-900 bg-slate-900 text-white dark:border-white dark:bg-white dark:text-slate-900"
          : "border-black/10 dark:border-white/10 bg-white dark:bg-slate-950 hover:bg-black/5 dark:hover:bg-white/10"
      }`}
    >
      <div className="font-semibold leading-tight truncate">{title}</div>
      <div
        className={`mt-0.5 text-sm leading-snug ${
          active
            ? "text-white/80 dark:text-slate-600"
            : "text-slate-500 dark:text-slate-400"
        }`}
      >
        {desc}
      </div>
    </button>
  );

  return (
    <div className="space-y-5 font-roboto min-w-0 w-full max-w-full">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 min-w-0">
        <div className="min-w-0">
          <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-white">
            Montant & méthode
          </h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            Choisissez ce que vous envoyez ou ce que le bénéficiaire doit
            recevoir.
          </p>
        </div>

        <div className="w-full sm:w-auto min-w-0 rounded-2xl border border-black/5 dark:border-white/10 bg-slate-50 dark:bg-white/5 px-3 py-2 overflow-hidden">
          <p className="text-xs font-medium text-slate-700 dark:text-slate-200 break-words">
            Taux : 1 {currencyFromU} = {Number(displayRate).toFixed(6)}{" "}
            {recipientCurrencyU}
          </p>
          <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400 break-words">
            Source : {calculation?.rateSource || "fixed"}
          </p>
        </div>
      </div>

      {corridorHint && (
        <div className="rounded-2xl border border-blue-500/20 bg-blue-50 dark:bg-blue-900/20 px-4 py-3 text-sm text-blue-700 dark:text-blue-200">
          {corridorHint}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0">
        <PillButton
          active={mode === "SEND"}
          title="Montant à envoyer"
          desc="Je saisis le montant que j’envoie"
          onClick={() =>
            setFormData((p) => ({
              ...p,
              mode: "SEND",
              calculation: null,
            }))
          }
        />
        <PillButton
          active={mode === "RECEIVE"}
          title="Montant à recevoir"
          desc="Je saisis ce que le bénéficiaire doit recevoir"
          onClick={() =>
            setFormData((p) => ({
              ...p,
              mode: "RECEIVE",
              calculation: null,
            }))
          }
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 min-w-0">
        <Field
          label={`Vous envoyez (${currencyFromU})`}
          required
          helper={
            mode === "RECEIVE"
              ? "En mode “à recevoir”, ce champ affiche le montant de base à envoyer."
              : undefined
          }
        >
          <input
            type="number"
            inputMode="decimal"
            value={amountToSend || ""}
            disabled={mode === "RECEIVE"}
            onChange={(e) =>
              setFormData((p) => ({ ...p, amountToSend: e.target.value }))
            }
            className="w-full max-w-full min-w-0 bg-transparent outline-none text-lg font-semibold text-slate-900 dark:text-white disabled:text-slate-400"
            min={0}
          />
        </Field>

        <Field
          label={`Le bénéficiaire reçoit (${recipientCurrencyU})`}
          required
        >
          <input
            type="number"
            inputMode="decimal"
            value={amountToReceive || ""}
            disabled={mode === "SEND"}
            onChange={(e) =>
              setFormData((p) => ({ ...p, amountToReceive: e.target.value }))
            }
            className="w-full max-w-full min-w-0 bg-transparent outline-none text-lg font-semibold text-emerald-700 dark:text-emerald-300 disabled:text-slate-400"
            min={0}
          />
        </Field>
      </div>

      <div className="space-y-2 min-w-0">
        <label className="text-sm font-medium text-slate-700 dark:text-slate-200">
          Méthode de retrait <span className="text-red-500">*</span>
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0">
          {methods.map((m) => (
            <label
              key={m}
              className="min-w-0 w-full flex items-center gap-2 rounded-2xl border border-black/5 dark:border-white/10
                         bg-white dark:bg-slate-950 px-4 py-3 text-sm overflow-hidden"
            >
              <input
                type="radio"
                checked={method === m}
                onChange={() =>
                  setFormData((p) => ({ ...p, method: m, calculation: null }))
                }
              />
              <span className="font-medium truncate">
                {m === "INTERAC"
                  ? "Interac"
                  : m === "MOBILE_MONEY"
                    ? "Mobile Money"
                    : "Virement bancaire"}
              </span>
            </label>
          ))}
        </div>
      </div>

      {calculation && (
        <div className="rounded-2xl border border-black/5 dark:border-white/10 bg-slate-50 dark:bg-white/5 p-4 text-sm space-y-3 min-w-0 overflow-hidden">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-2 text-slate-700 dark:text-slate-200">
            <span>Frais</span>
            <strong className="sm:text-right break-words text-emerald-600 font-bold">
              {Number(calculation.fee).toFixed(2)}{" "}
              {calculation.currencyFrom || currencyFromU}
            </strong>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-2 text-slate-600 dark:text-slate-300">
            <span>Total à payer</span>
            <strong className="sm:text-right break-words text-emerald-600 font-extrabold">
              {Number(calculation.totalToPay).toFixed(2)}{" "}
              {calculation.currencyFrom || currencyFromU}
            </strong>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-2 text-slate-600 dark:text-slate-300">
            <span>Montant reçu</span>
            <strong className="sm:text-right break-words text-emerald-700 dark:text-emerald-300 font-extrabold">
              {Number(calculation.convertedAmount).toFixed(2)}{" "}
              {calculation.recipientCurrency || recipientCurrencyU}
            </strong>
          </div>
        </div>
      )}

      {loading && (
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Calcul en cours…
        </p>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 min-w-0">
        <button
          type="button"
          onClick={onPrev}
          className="w-full rounded-2xl border border-black/10 dark:border-white/10 bg-white dark:bg-slate-950 py-3 font-medium
                     text-slate-900 dark:text-white hover:bg-black/5 dark:hover:bg-white/10 transition"
        >
          Retour
        </button>

        <button
          type="button"
          onClick={onNext}
          disabled={!canContinue}
          className="w-full rounded-2xl bg-slate-900 text-white py-3 font-medium hover:opacity-95 transition
                     disabled:opacity-50 disabled:cursor-not-allowed dark:bg-white dark:text-slate-900"
        >
          Continuer
        </button>
      </div>
    </div>
  );
}

function Field({ label, required, helper, children }) {
  return (
    <div className="rounded-2xl border border-black/5 dark:border-white/10 bg-white dark:bg-slate-950 p-4 min-w-0 overflow-hidden">
      <label className="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {helper && (
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          {helper}
        </p>
      )}
    </div>
  );
}
